import { defineStore } from 'pinia'
import { useUserStore } from './user'
import dashboardService from '@/services/dashboard-service'
import stripeService from '@/services/stripe-service'

let currentUpdatePromise = null;
let lastLocale = null;

function getCurrentLanguage() {
  let language = window.navigator.userLanguage || window.navigator.language

  if (language == 'pt' || language.startsWith('pt-'))
    return 'BR'
  else
    return 'en'
}

export const useSubscriptionStore = defineStore('subscription', {
  state: () => ({
    clinicUsage: {
      percentage: 0,
      imageTotal: 0,
      planLimit: 0,
      subscriptionName: '',
      displayName: '',
      fullSubscription: null,
      isExpired: false,
      needsResubscription: false,
      migrated: true,
      hasLegacyCredit: false,
      legacyPlanCurrency: null
    },
    allProducts: [],
    loadingProducts: false,
  }),

  actions: {
    async updateUsage(locale) {
      const activeLocale = locale || getCurrentLanguage();

      if (currentUpdatePromise && lastLocale === activeLocale)
        return currentUpdatePromise;

      const userStore = useUserStore();
      const clinic = userStore.sessionInfo?.currentClinic;

      if (!clinic?.id)
        return null;

      lastLocale = activeLocale;
      const paramsLocale = { locale: activeLocale };

      currentUpdatePromise = (async () => {
        try {
          const params = { id: clinic.id };

          const [usageRes, stripeRes, productsRes] = await Promise.all([
            dashboardService.getClinicUsage(params),
            stripeService.getClinicSubscription({ clinicUUID: clinic.uuid }),
            stripeService.list(paramsLocale)
          ]);

          if (productsRes.result?.data)
            this.allProducts = [...productsRes.result.data].reverse();

          const activeSubscription = stripeRes.subscriptions && stripeRes.subscriptions.length > 0 ? stripeRes.subscriptions[0] : null;

          const subName = activeSubscription?.product_name || 'Starter';
          const productsMap = {};
          this.allProducts.forEach(p => { productsMap[p.name] = p; });

          const used = usageRes.clinicUsage?.imageTotal || 0;
          const backofficeLimit = usageRes.clinicUsage?.quotaAvailable;
          const stripeLimit = dashboardService.getPlanLimit(subName, productsMap);
          
          let adjustedStripeLimit = stripeLimit;
          if (adjustedStripeLimit !== 'unlimited' && subName && subName !== 'Starter') {
            adjustedStripeLimit = Number(adjustedStripeLimit) + 10000;
          }

          const finalLimit = (backofficeLimit !== undefined && backofficeLimit !== null) ? backofficeLimit : adjustedStripeLimit;

          this.clinicUsage.fullSubscription = activeSubscription;
          this.clinicUsage.subscriptionName = subName;
          // Text only: a name set in BO2 is shown instead of the Stripe plan name (or "Starter"). The backend
          // clears it when the plan changes on Stripe. subscriptionName stays the real plan name, since the
          // "Current" flag, the plan limit and the Starter/Corporate checks match by it.
          this.clinicUsage.displayName = usageRes.clinicUsage?.planDisplayName || subName;
          this.clinicUsage.imageTotal = used;
          this.clinicUsage.planLimit = finalLimit;
          this.clinicUsage.percentage = dashboardService.getPercentageValue(used, finalLimit);
          // All three set by the backend (clinic-subscription.js). `migrated` is
          // true only if the clinic has a real subscription on the new (CNPJ)
          // Stripe account - false for anyone still relying on the legacy
          // Subscription record, regardless of whether that legacy period has
          // expired yet. `isExpired` is the legacy period lapsing, used to
          // adjust what the plan page shows. `needsResubscription` is
          // narrower still - isExpired minus a voluntary cancellation or an
          // explicit decline - used to force the redirect to /plans.
          this.clinicUsage.migrated = !!stripeRes.migrated;
          this.clinicUsage.isExpired = !!stripeRes.isExpired;
          this.clinicUsage.needsResubscription = !!stripeRes.needsResubscription;
          this.clinicUsage.hasLegacyCredit = !!stripeRes.hasLegacyCredit;
          // Currency the legacy plan was paid in (null when unknown). Only sent for not-migrated clinics.
          this.clinicUsage.legacyPlanCurrency = stripeRes.legacyPlanCurrency || null;

          return {
            ...this.clinicUsage,
            subscription: activeSubscription
          };
        } catch (error) {
          console.error("Erro ao atualizar dados de assinatura:", error);
          throw error;
        } finally {
          currentUpdatePromise = null;
        }
      })();

      return currentUpdatePromise;
    },

    async fetchProducts(locale, force = false) {
      const activeLocale = locale || getCurrentLanguage();

      if (this.loadingProducts || (this.allProducts.length > 0 && !force))
        return;

      this.loadingProducts = true;
      try {
        const paramsLocale = { locale: activeLocale };
        const response = await stripeService.list(paramsLocale);
        this.allProducts = response.result.data.reverse();
      } finally {
        this.loadingProducts = false;
      }
    }
  },
})
